import React, { forwardRef, useId, useState } from "react";
import { motion } from "framer-motion";
import { Eye, EyeSlash, WarningCircle, CheckCircle } from "@phosphor-icons/react";

/**
 * Input moderno com floating label + estados de erro/sucesso.
 * IMPORTANT: usa placeholder=" " (não vazio!) para o CSS de floating label funcionar
 * via seletor `:not(:placeholder-shown)`.
 */
const ModernInput = forwardRef(function ModernInput(
  {
    label,
    type = "text",
    value,
    onChange,
    error,
    success,
    hint,
    autoComplete,
    inputMode,
    required,
    disabled,
    testid,
    icon: RightIcon,
    ...rest
  },
  ref
) {
  const id = useId();
  const [showPw, setShowPw] = useState(false);
  const isPw = type === "password";
  const effType = isPw && showPw ? "text" : type;

  return (
    <div className="mod-input-wrap" data-testid={testid ? `${testid}-wrap` : undefined}>
      <input
        ref={ref}
        id={id}
        type={effType}
        value={value}
        onChange={onChange}
        placeholder=" "
        autoComplete={autoComplete}
        inputMode={inputMode}
        required={required}
        disabled={disabled}
        data-testid={testid}
        className={`mod-input ${error ? "mod-input-err" : ""} ${(isPw || RightIcon || success) ? "pr-11" : ""}`}
        {...rest}
      />
      <label htmlFor={id} className="mod-input-label">
        {label}{required && <span className="text-[var(--accent)] ml-0.5">*</span>}
      </label>

      {/* Ícone à direita */}
      {isPw && (
        <button
          type="button"
          onClick={() => setShowPw((v) => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors p-1"
          tabIndex={-1}
          aria-label={showPw ? "Ocultar senha" : "Mostrar senha"}
          data-testid={testid ? `${testid}-toggle` : undefined}
        >
          {showPw ? <EyeSlash size={18} weight="regular" /> : <Eye size={18} weight="regular" />}
        </button>
      )}
      {!isPw && success && (
        <CheckCircle
          size={20}
          weight="fill"
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--success)] pointer-events-none"
        />
      )}
      {!isPw && !success && RightIcon && (
        <RightIcon
          size={18}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none"
        />
      )}

      {/* Mensagens */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.18 }}
          className="mt-1.5 flex items-start gap-1 text-xs text-[var(--danger)]"
          data-testid={testid ? `${testid}-error` : undefined}
        >
          <WarningCircle size={12} weight="fill" className="mt-0.5 shrink-0" />
          <span className="wrap-safe">{error}</span>
        </motion.div>
      )}
      {!error && hint && (
        <div className="mt-1.5 text-xs text-white/40 wrap-safe">{hint}</div>
      )}
    </div>
  );
});

export default ModernInput;
